import { Box, Flex, font, useClickOutside } from '@lemonade-hq/cdk';
import { FatSmallBottomChevronIcon, MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { Faq } from 'contentlayer/generated';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-scroll';
import styled from 'styled-components';

const Wrapper = styled(Flex)`
  position: sticky;
  width: 100%;
  max-width: 340px;
  top: 120px;
  height: auto;
  max-height: calc(100vh - 140px);

  ${MEDIA_QUERIES.mobile`
    position: fixed;
    top: 70px;
    left: 0;
    max-width: 100%;
    overflow: auto;
    padding-bottom: 30px;
  `}
`;

const Aside = styled.aside`
  ${font('main', {
    fontSize: '1.6rem',
    lineHeight: '2.4rem',
  })};
  width: 100%;
  overflow: scroll;
  height: calc(100vh - 180px);
`;

const DesktopList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0;
  margin: 0;
  margin-top: 10px;
  padding-right: 30px;

  li {
    font-weight: 700;
    cursor: pointer;
    user-select: none;
    color: ${({ theme }) => theme.colors.primary};
    &::before {
      content: '';
      display: inline-block;
      height: 0;
      width: 0;
      border-top: 4px solid ${({ theme }) => theme.colors.primary};
      border-bottom: 0px solid ${({ theme }) => theme.colors.white};
      border-right: 4px solid ${({ theme }) => theme.colors.white};
      border-left: 4px solid ${({ theme }) => theme.colors.white};
      margin-right: 10px;
      transform: rotate(-90deg) translate(50%, 0);
      transform-origin: center center;
      transition: transform 0.15s;
    }

    &[data-active='true'] {
      &::before {
        transform: rotate(0deg) translate(0, -50%);
      }
      ul {
        display: flex;
      }
    }
  }
`;

const InternalDesktopList = styled.ul`
  display: none;
  flex-direction: column;
  gap: 0px;
  overflow: hidden;
  margin-top: 0px;

  li:first-child a {
    padding-top: 20px;
  }

  li {
    &::before {
      display: none;
    }
    a {
      font-weight: 400;
      display: block;
      color: ${({ theme }) => theme.colors.boulder};
      scroll-behavior: smooth;
      cursor: pointer;
      padding-left: 30px;
      padding-top: 6px;
      padding-bottom: 8px;

      &.active {
        color: ${({ theme }) => theme.colors.primary};
      }
    }
  }
`;

const MobileMenuActionWrapper = styled(Flex)`
  width: 100%;
  background: ${({ theme }) => theme.colors.white};
  padding: 5px 20px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  user-select: none;
  ${font('main', {
    fontSize: '1.8rem',
    lineHeight: '2.4rem',
  })};
`;

const MobileMenuAction = styled.button`
  ${font('main', {
    fontSize: '1.7rem',
    lineHeight: '2.4rem',
  })};
  color: ${({ theme }) => theme.colors.textPrimary};
  background: none;
  height: 54px;
  border: solid 1px ${({ theme }) => theme.colors.mercury};
  cursor: pointer;
  text-align: left;
  width: 100%;
  padding: 0;
  margin: 0;
  outline: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
`;

const Icon = styled(FatSmallBottomChevronIcon)<{ active: boolean }>`
  position: absolute;
  top: 22px;
  right: 35px;
  pointer-events: none;
  transition: transform 0.2s ease-in-out;
  transform: ${({ active }) => (active ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const TopBar = styled.div<{ shown: boolean }>`
  ${font('main')};
  width: calc(100% - 40px);
  background: white;
  margin-left: 20px;
  display: ${({ shown }) => (shown ? 'block' : 'none')};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  border-radius: 0 0 12px 12px;
`;

const MobileList = styled.ul`
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: left;

  li a {
    display: block;
    font-size: 1.7rem;
    border-bottom: solid 1px ${({ theme }) => theme.colors.mercury};
    white-space: nowrap;
    padding: 18px 20px;
    color: ${({ theme }) => theme.colors.boulder};
    &.active {
      color: ${({ theme }) => theme.colors.primary};
    }

    &[data-active='true'] {
      font-weight: 700;
    }
  }

  li:last-child a {
    border-bottom: none;
  }
`;

const MenuMobile: React.FC<{ content: Faq }> = ({ content }) => {
  const ref = useRef(null);
  const mobile = content?.mobileMenu;

  const [menu, setMenu] = useState({
    shown: false,
    name: (mobile ?? [])[0]?.name ?? '',
  });

  useClickOutside(ref, menu.shown, () => setMenu(s => ({ ...s, shown: false })));

  const onSectActive = useCallback(
    (section: string) => {
      const activeLink = mobile?.find(link => link.url === section)?.name ?? '';

      setMenu(s => ({
        ...s,
        name: activeLink,
      }));
    },
    [mobile],
  );

  const onClick = useCallback(
    (section: string) => {
      const activeLink = mobile?.find(link => link.url === section)?.name ?? '';

      setMenu(s => ({
        ...s,
        name: activeLink,
        shown: false,
      }));
    },
    [mobile],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box display={['block', 'none']} width="100%" ref={ref}>
      <MobileMenuActionWrapper>
        <MobileMenuAction onClick={() => setMenu(s => ({ ...s, shown: !s.shown }))}>{menu.name}</MobileMenuAction>
        <Icon active={menu.shown} />
      </MobileMenuActionWrapper>

      <TopBar shown={menu.shown}>
        <MobileList>
          {mobile?.map(link => (
            <li key={link.name}>
              <Link
                className={menu.name === link.name ? 'active' : ''}
                offset={-170}
                to={link.url}
                spy
                hashSpy
                onSetActive={onSectActive}
                onClick={() => onClick(link.url)}
                smooth
                href={`#${link.url}`}
              >
                {link.name}
              </Link>
            </li>
          ))}
        </MobileList>
      </TopBar>
    </Box>
  );
};

const MenuDesktop: React.FC<{ content: Faq }> = ({ content }) => {
  const desktop = content?.desktopMenu;
  const [menu, setMenu] = useState<{ category: string[]; link: string }>({
    category: [],
    link: '',
  });

  const onClickCategory = useCallback(
    (category: string) => {
      const isCategoryActive = menu.category.includes(category);
      setMenu(s => ({
        ...s,
        category: isCategoryActive ? s.category.filter(item => item !== category) : [...s.category, category],
      }));
    },
    [menu.category],
  );

  const onSectActive = useCallback(
    (section: string) => {
      const openCategory = desktop
        ?.filter(({ links }) => links.some(link => link.url === section))
        .map(({ category }) => category) as string[];

      setMenu(s => ({
        ...s,
        category: [...s.category, ...openCategory],
        link: section,
      }));
    },
    [desktop],
  );

  return (
    <Box display={['none', 'block']} width="100%">
      <Aside>
        <DesktopList>
          {desktop?.map(({ category, links }) => (
            <li
              key={category}
              data-active={menu.category.includes(category).toString()}
              onClick={() => onClickCategory(category)}
            >
              <span>{category}</span>
              <InternalDesktopList onClick={e => e.stopPropagation()}>
                {links.map(link => (
                  <li key={`${category}-${link.name}`}>
                    <Link
                      className={menu.link === link.url ? 'active' : ''}
                      offset={-100}
                      to={link.url}
                      spy
                      hashSpy
                      onSetActive={onSectActive}
                      smooth
                      href={`#${link.url}`}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </InternalDesktopList>
            </li>
          ))}
        </DesktopList>
      </Aside>
    </Box>
  );
};

export const FaqPageMenu: React.FC<{ content: Faq }> = ({ content }) => {
  return (
    <Wrapper>
      <MenuDesktop content={content} />
      <MenuMobile content={content} />
    </Wrapper>
  );
};
